import {
  JSONPatchDocument,
  LookupTrackerStore,
  Model,
  ModelWithMeta,
  QueryParams,
} from "@ofqual/portal-core/core/types";
import { AnyAction } from "redux";
import { AdditionalEvidenceRequest } from "../domain/AdditionalEvidenceRequest";
import { Attachment } from "../domain/Attachment";
import { DateEvent } from "../domain/DateEvent";
import { Scope } from "../domain/Scope";
import { ScopeApplication } from "../domain/ScopeApplication";
import { MODULE_NAME } from "../moduleName";

export interface ActionPayload<T> extends AnyAction {
  type: string;
  payload: T;
}

export const LOADED_ITEM = `${MODULE_NAME}/LOADED_ITEM`;
export const LOADING_ITEM = `${MODULE_NAME}/LOADING_ITEM`;
export const SAVED_ITEM = `${MODULE_NAME}/SAVED_ITEM`;
export const RESET_ITEM = `${MODULE_NAME}/RESET_ITEM`;
export const LOADED_EVIDENCE = `${MODULE_NAME}/LOADED_EVIDENCE`;

export const UPDATING_ITEM = `${MODULE_NAME}/UPDATING_ITEM`;
export const UPDATING_ITEM_VALIDATION_FAIL = `${MODULE_NAME}/UPDATING_ITEM_VALIDATION_FAIL`;

export const SUBMITTING_UPDATE_ITEM = `${MODULE_NAME}/SUBMITTING_UPDATE_ITEM`;
export const SUBMITTING_UPDATE_ITEM_VALIDATION_FAIL = `${MODULE_NAME}/SUBMITTING_UPDATE_ITEM_VALIDATION_FAIL`;
export const SUBMITTED_UPDATE_ITEM = `${MODULE_NAME}/SUBMITTED_UPDATE_ITEM`;

export const INPUTTING_ITEM = `${MODULE_NAME}/INPUTTING_ITEM`;
export const CREATING_ITEM = `${MODULE_NAME}/CREATING_ITEM`;
export const CREATING_ITEM_VALIDATION_FAIL = `${MODULE_NAME}/CREATING_ITEM_VALIDATION_FAIL`;
export const CREATED_ITEM = `${MODULE_NAME}/CREATED_ITEM`;

export const SUBMITTING_ITEM = `${MODULE_NAME}/SUBMITTING_ITEM`;
export const SUBMITTING_ITEM_VALIDATION_FAIL = `${MODULE_NAME}/SUBMITTING_ITEM_VALIDATION_FAIL`;
export const SUBMITTED_ITEM = `${MODULE_NAME}/SUBMITTED_ITEM`;

export const ADD_SCOPE = `${MODULE_NAME}/ADD_SCOPE`;
export const TOGGLE_DELETE_SCOPE = `${MODULE_NAME}/TOGGLE_DELETE_SCOPE`;
export const DELETE_SCOPES = `${MODULE_NAME}/DELETE_SCOPES`;
export const UPDATE_SCOPE = `${MODULE_NAME}/UPDATE_SCOPE`;
export const UPDATING_SCOPES = `${MODULE_NAME}/UPDATING_SCOPES`;

export const TOGGLE_DELETE_ATTACHMENT = `${MODULE_NAME}/TOGGLE_DELETE_ATTACHMENT`;
export const DELETE_ATTACHMENT = `${MODULE_NAME}/DELETE_ATTACHMENT`;

export const ASSIGNING_ITEM = `${MODULE_NAME}/ASSIGNING_ITEM`;
export const SENDING_NOTE = `${MODULE_NAME}/SENDING_NOTE`;
export const SENDING_DOCUMENTS = `${MODULE_NAME}/SENDING_DOCUMENTS`;
export const SENDING_DATE = `${MODULE_NAME}/SENDING_DATE`;
export const CLOSING_APPLICATION = `${MODULE_NAME}/CLOSING_APPLICATION`;
export const SENDING_EVIDENCE = `${MODULE_NAME}/SENDING_EVIDENCE`;
export const SENDING_MESSAGE = `${MODULE_NAME}/SENDING_MESSAGE`;
export const WITHDRAWING_APPLICATION = `${MODULE_NAME}/WITHDRAWING_APPLICATION`;

export const LOADING_EVIDENCE_REQUEST = `${MODULE_NAME}/LOADING_EVIDENCE_REQUEST`;
export const LOADED_EVIDENCE_REQUEST = `${MODULE_NAME}/LOADED_EVIDENCE_REQUEST`;
export const UPDATING_EVIDENCE_REQUEST = `${MODULE_NAME}/UPDATING_EVIDENCE_REQUEST`;
export const UPDATING_OTHER_EVIDENCE_REQUEST = `${MODULE_NAME}/UPDATING_OTHER_EVIDENCE_REQUEST`;
export const DELETE_EVIDENCE_ATTACHMENT = `${MODULE_NAME}/DELETE_EVIDENCE_ATTACHMENT`;
export const SUBMITTING_EVIDENCE = `${MODULE_NAME}/SUBMITTING_EVIDENCE`;

export const LOADED_ORGANISATION_SCOPES = `${MODULE_NAME}/LOADED_ORGANISATION_SCOPES`;
export const LOADING_ORGANISATION_SCOPES = `${MODULE_NAME}/LOADING_ORGANISATION_SCOPES`;
export const UPDATE_ORGS_PARAMS = `${MODULE_NAME}/UPDATE_ORGS_PARAMS`;

export const loadedItem = (
  item: ScopeApplication,
  scopes: Scope[],
  attachments: Attachment[],
  evidenceRequests?: AdditionalEvidenceRequest[]
) => ({
  type: LOADED_ITEM,
  payload: { item, scopes, attachments, evidenceRequests },
});

export const loadedAdditionalEvidence = (
  evidenceRequests: AdditionalEvidenceRequest[]
) => ({
  type: LOADED_EVIDENCE,
  payload: evidenceRequests,
});

export const savedItem = (item: ScopeApplication) => ({
  type: SAVED_ITEM,
  payload: item,
});

export const resetItem = () => ({
  type: RESET_ITEM,
});

export const loadingItem = (orgId: string, id: string) => ({
  type: LOADING_ITEM,
  payload: { orgId, id },
});

export const submitUpdateItem = (
  orgId: string,
  id: string,
  patchDocument: JSONPatchDocument,
  scopes: Scope[],
  documentsToDelete: Attachment[]
) => ({
  type: SUBMITTING_UPDATE_ITEM,
  payload: { orgId, id, patchDocument, scopes, documentsToDelete },
});

export const updatingItem = (
  orgId: string,
  id: string,
  patchDocument: JSONPatchDocument,
  scopes: Scope[],
  documentsToDelete: Attachment[]
) => ({
  type: UPDATING_ITEM,
  payload: { orgId, id, patchDocument, scopes, documentsToDelete },
});

export const createItem = (
  orgId: string,
  item: ScopeApplication,
  scopes: Scope[]
) => ({
  type: CREATING_ITEM,
  payload: { orgId, item, scopes },
});

export const submitItem = (
  orgId: string,
  item: ScopeApplication,
  scopes: Scope[]
) => ({
  type: SUBMITTING_ITEM,
  payload: { orgId, item, scopes },
});

export const inputtingItem = () => ({
  type: INPUTTING_ITEM,
});

export const addScope = (payload: Scope) => ({
  type: ADD_SCOPE,
  payload,
});

export const toggleDeleteScope = (scopes: Scope[]) => ({
  type: TOGGLE_DELETE_SCOPE,
  payload: {
    scopes,
  },
});

export const deleteSelectedScopes = () => ({
  type: DELETE_SCOPES,
});

export const updateScope = (
  updatedScope: Scope,
  lookupTrackingInfo: LookupTrackerStore
) => ({
  type: UPDATE_SCOPE,
  payload: {
    updatedScope,
    lookupTrackingInfo,
  },
});

export const updatingScopes = (
  orgId: string,
  id: string,
  modelsWithMeta: ModelWithMeta<Scope>[],
  message: string
) => ({
  type: UPDATING_SCOPES,
  payload: {
    orgId,
    id,
    modelsWithMeta,
    message,
  },
});

export const deleteSelectedAttachment = (attachment: Attachment) => ({
  type: DELETE_ATTACHMENT,
  payload: {
    attachment,
  },
});

export const assignItem = (
  orgId: string,
  id: string,
  assignedToUpn: string,
  assignedToDisplayName: string,
  message: string
) => ({
  type: ASSIGNING_ITEM,
  payload: { orgId, id, assignedToUpn, assignedToDisplayName, message },
});

export const sendNote = (orgId: string, id: string, note: string) => ({
  type: SENDING_NOTE,
  payload: { orgId, id, note },
});

export const sendDocuments = (
  orgId: string,
  id: string,
  files: File[],
  documentType: string,
  note: string
) => ({
  type: SENDING_DOCUMENTS,
  payload: { orgId, id, files, documentType, note },
});

export const sendDate = (orgId: string, id: string, dateEvent: DateEvent) => ({
  type: SENDING_DATE,
  payload: { orgId, id, dateEvent },
});

export const closeApplication = (
  orgId: string,
  id: string,
  note: string,
  letter: File
) => ({
  type: CLOSING_APPLICATION,
  payload: { orgId, id, note, letter },
});

export const sendRequestEvidence = (
  orgId: string,
  id: string,
  evidenceEvent: Model
) => ({
  type: SENDING_EVIDENCE,
  payload: { orgId, id, evidenceEvent },
});

export const sendMessage = (orgId: string, id: string, message: string) => ({
  type: SENDING_MESSAGE,
  payload: { orgId, id, message },
});

export const withdrawApplication = (
  orgId: string,
  id: string,
  note: string
) => ({
  type: WITHDRAWING_APPLICATION,
  payload: { orgId, id, note },
});

export const loadingEvidenceRequest = (
  orgId: string,
  id: string,
  requestId: string
) => ({
  type: LOADING_EVIDENCE_REQUEST,
  payload: { orgId, id, requestId },
});

export const loadedEvidenceRequest = (item: AdditionalEvidenceRequest) => ({
  type: LOADED_EVIDENCE_REQUEST,
  payload: item,
});

export const updatingEvidence = (
  orgId: string,
  id: string,
  requestId: string,
  evidence: AdditionalEvidenceRequest
) => ({
  type: UPDATING_EVIDENCE_REQUEST,
  payload: { orgId, id, requestId, evidence },
});

export const updatingOtherEvidence = (
  orgId: string,
  id: string,
  requestId: string,
  files: File[],
  evidence: AdditionalEvidenceRequest
) => ({
  type: UPDATING_OTHER_EVIDENCE_REQUEST,
  payload: { orgId, id, requestId, files, evidence },
});

export const deleteEvidenceAttachment = (attachment: Attachment) => ({
  type: DELETE_EVIDENCE_ATTACHMENT,
  payload: {
    attachment,
  },
});

export const submitEvidence = (
  orgId: string,
  id: string,
  requestId: string,
  timestamp: string
) => ({
  type: SUBMITTING_EVIDENCE,
  payload: { orgId, id, requestId, timestamp },
});

export const loadedOrganisationScopes = (payload: Scope[]) => ({
  type: LOADED_ORGANISATION_SCOPES,
  payload,
});

export const loadingOrganisationScopes = (payload: string) => ({
  type: LOADING_ORGANISATION_SCOPES,
  payload,
});

export const updateOrganisationScopesParams = (newParams: QueryParams) => ({
  type: UPDATE_ORGS_PARAMS,
  payload: newParams,
});
